import "core-js/modules/es6.symbol";
import "core-js/modules/es6.array.index-of";
import "core-js/modules/es6.object.assign";
import "core-js/modules/es6.object.define-property";

var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }

  return cooked;
};

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import { TextAlign as Align, VerticalAlign } from '../../../types/css';
import { FunctionalColor as Color } from '../../../types/color';
import { css, styled } from '../../../utilities/styled-components';
import { filterForAriaProps } from '../../../utilities/accessibility';
import { getLineHeightCSS, getResponsiveTextSize, getResponsiveTextSizeCSS } from '../../typography/Text/responsive-utils';
import { HTMLType, Variant } from '../../../types/text-button';
import { isLeftClickEvent, isModifiedEvent, resolveToLocation } from './utils';
import { isResponsive } from '../../../utilities/responsive';
import { MainColorConsumer } from '../../theme/MainColorProvider';
import { Size, Weight } from '../../../types/text';
import { Target } from '../../../types/target';
var routerShape = PropTypes.shape({
  push: PropTypes.func.isRequired,
  replace: PropTypes.func,
  go: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goForward: PropTypes.func.isRequired,
  setRouteLeaveHook: PropTypes.func.isRequired,
  isActive: PropTypes.func.isRequired
});
export var InnerTextButton = function InnerTextButton(_a, context) {
  var align = _a.align,
      ariaLabel = _a.ariaLabel,
      children = _a.children,
      color = _a.color,
      dataTag = _a["data-tag"],
      disabled = _a.disabled,
      fluid = _a.fluid,
      href = _a.href,
      id = _a.id,
      inline = _a.inline,
      innerRef = _a.innerRef,
      internal = _a.internal,
      italic = _a.italic,
      lineHeight = _a.lineHeight,
      onClick = _a.onClick,
      _b = _a.size,
      size = _b === void 0 ? Size.Md : _b,
      sizeRem = _a.sizeRem,
      target = _a.target,
      title = _a.title,
      _c = _a.type,
      type = _c === void 0 ? HTMLType.Button : _c,
      uppercase = _a.uppercase,
      underline = _a.underline,
      weight = _a.weight,
      variant = _a.variant,
      _d = _a.verticalAlign,
      verticalAlign = _d === void 0 ? VerticalAlign.Bottom : _d,
      restProps = __rest(_a, ["align", "ariaLabel", "children", "color", 'data-tag', "disabled", "fluid", "href", "id", "inline", "innerRef", "internal", "italic", "lineHeight", "onClick", "size", "sizeRem", "target", "title", "type", "uppercase", "underline", "weight", "variant", "verticalAlign"]);

  var handleClick = function handleClick(e) {
    /* css pointer-events handles this for most browsers, but need this for IE10 -gb */
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(e);
    }

    if (!href || !internal) {
      return;
    }
    /* The following is adapted from https://github.com/ReactTraining/react-router/blob/42fe32d8f4ebec170a6d980badbc702ab88b5897/modules/Link.js#L67-L91
        and should be updated when we update `react-router` past 3.2.0 */


    if (e.defaultPrevented) {
      return;
    }

    var router = context.router;
    invariant(router, '<Link>s rendered outside of a router context cannot navigate.'); // if this isn't a vanilla left click event, let the browser handle it.

    if (isModifiedEvent(e) || !isLeftClickEvent(e)) {
      return;
    } // If target prop is set (e.g. to "_blank"), let the browser handle it.


    if (target) {
      return;
    }

    e.preventDefault();
    router.push(resolveToLocation(href, router));
  };

  var isActingAsButton = onClick && !href;

  var props = __assign({
    align: align,
    'aria-label': ariaLabel,
    colorValue: color,
    'data-tag': dataTag,
    disabled: disabled,
    fluid: fluid,
    href: href && resolveToLocation(href, context.router),
    id: id,
    inline: inline,
    internal: internal,
    isActingAsButton: isActingAsButton,
    italic: italic,
    lineHeight: lineHeight,
    onClick: handleClick,
    responsiveTextSize: !isResponsive(size) ? getResponsiveTextSize(size) : size,
    sizeRem: sizeRem,
    target: target,
    title: title,
    underline: underline,
    uppercase: uppercase,
    variant: variant,
    verticalAlign: verticalAlign,
    weight: weight
  }, filterForAriaProps(restProps));

  return React.createElement(MainColorConsumer, null, function (mainColor) {
    return isActingAsButton ? React.createElement(CommonButton, __assign({}, props, {
      mainColor: mainColor,
      ref: innerRef,
      tabIndex: 0,
      type: type
    }), children) : React.createElement(CommonButton, __assign({}, props, {
      mainColor: mainColor,
      ref: innerRef,
      rel: props.target === Target.Blank ? 'noopener noreferrer' : undefined,
      tabIndex: !props.href ? 0 : undefined,
      as: "a"
    }), children);
  });
};
InnerTextButton.contextTypes = {
  router: routerShape
};
InnerTextButton.displayName = 'InnerTextButton';
var TextButtonForwardRef = React.forwardRef(function (props, ref) {
  return React.createElement(InnerTextButton, __assign({}, props, {
    innerRef: ref
  }));
});
export var TextButton = Object.assign(TextButtonForwardRef, {
  displayName: 'TextButton',
  Align: Align,
  HTMLType: HTMLType,
  Color: Color,
  Size: Size,
  Target: Target,
  Variant: Variant,
  VerticalAlign: VerticalAlign,
  Weight: Weight
});
var CommonButton = styled.button(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    color: ", ";\n    ", "\n    cursor: pointer;\n    display: ", ";\n    ", ";\n    ", ";\n    ", ";\n    font-weight: ", ";\n    max-width: 100%;\n    ", ";\n    overflow: hidden;\n    text-decoration: ", ";\n    text-decoration-skip: ink;\n    text-align: ", ";\n    text-overflow: ellipsis;\n    ", ";\n    transition: ", ";\n    vertical-align: ", ";\n    ", ";\n    ", ";\n    &:visited {\n        ", ";\n    }\n\n    &:hover {\n        ", ";\n    }\n"], ["\n    color: ", ";\n    ", "\n    cursor: pointer;\n    display: ", ";\n    ", ";\n    ", ";\n    ", ";\n    font-weight: ", ";\n    max-width: 100%;\n    ", ";\n    overflow: hidden;\n    text-decoration: ", ";\n    text-decoration-skip: ink;\n    text-align: ", ";\n    text-overflow: ellipsis;\n    ", ";\n    transition: ", ";\n    vertical-align: ", ";\n    ", ";\n    ", ";\n    &:visited {\n        ", ";\n    }\n\n    &:hover {\n        ", ";\n    }\n"])), function (props) {
  return props.theme.textButtons.getColor(props.colorValue, props.theme.colors, props.mainColor, props.variant);
}, function (props) {
  return props.isActingAsButton && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n            background: none;\n            border: none;\n            padding: 0;\n        "], ["\n            background: none;\n            border: none;\n            padding: 0;\n        "])));
}, function (props) {
  return props.inline ? 'inline' : 'inline-block';
}, function (props) {
  return props.disabled ? 'opacity: 0.33' : '';
}, function (props) {
  return props.italic ? 'font-style: italic' : '';
}, function (props) {
  return props.fluid ? 'width: 100%' : '';
}, function (props) {
  return props.weight ? props.theme.text.getWeight(props.weight) : props.theme.isRefresh ? props.theme.text.getWeight(Weight.Normal) : props.theme.text.getWeight(Weight.Bold) + " !important";
}, function (props) {
  return props.disabled ? 'pointer-events: none' : '';
}, function (props) {
  return props.theme.isRefresh && props.underline !== false || !props.theme.isRefresh && props.underline ? 'underline !important' : 'none';
}, function (props) {
  return props.align;
}, function (props) {
  return props.uppercase ? 'text-transform: uppercase' : '';
}, function (props) {
  return props.theme.transitions["default"];
}, function (props) {
  return props.verticalAlign;
}, function (props) {
  return getResponsiveTextSizeCSS(props.responsiveTextSize, props.theme, props.sizeRem);
}, function (props) {
  return getLineHeightCSS(props.lineHeight, props.responsiveTextSize, props.theme);
}, function (props) {
  return props.theme.textButtons.getVisitedStyles(props.colorValue, props.theme.colors, props.mainColor, props.variant);
}, function (props) {
  return !props.disabled ? props.theme.textButtons.hoverStyles : 'text-decoration: unset;';
});
var templateObject_1, templateObject_2;